import { MAGNITUDES, MAX_DECIMAL_PLACE, EXPONENT_NEGATIVE, EXPONENT_POSITIVE } from '../constants/config/numbers';

/**
 * @name convertMagnitude
 * @function
 * @description Converts a provided magnitude to a usable number (for functional purposes)
 * @param {string} mag Magnitude to be converted
 */
export const convertMagnitude = mag => {
    
    // if there's no magnitude for some reason, don't bother. (don't swap with `!mag` as that will be true for `0`)
    if( mag === null || mag === undefined ) return 0;

    const nonNumeric = /[^0-9\.-]/gi;

    let magnitude = MAGNITUDES.find( magnitude => mag.includes( magnitude.symbol ) ),

    // Replacing units like Hz, B etc. (if present), irrespective of the presence of symbol.
    multiplicand = magnitude ? Number( mag.replace( magnitude.symbol, '' ).replace( nonNumeric, '' ) ) : Number( mag.replace( nonNumeric, '' ) ),
    multiplier = 1;

    if ( magnitude ) multiplier = magnitude.divisor || magnitude.min;

    // since the values are until f which is till 10^-15, fixing till 16 digits
    return parseFloat( ( multiplicand * multiplier ).toFixed(16) ) || 0;
}

/**
 * @name isMagnitude
 * @function
 * @description Determines if the provided string is a magnitude
 * @param {string} str String to be tested
 * @returns {boolean} `true` if the string is a magnitude number, `false` if the string is not a magnitude
 */
export const isMagnitude = str => {
    if (typeof str !== 'string' || !str.length) {
        return false;
      }
    if( !isNaN( str ) ) return false;
    let length = ( str.length - 1 ),
    lastChar = str.charAt( length );
    return ( MAGNITUDES.some( magnitude => magnitude.symbol === lastChar ) && !isNaN( str.substr( 0, length ) ) );
}

/**
 * @name isExponential
 * @function
 * @description Determines if a number is expressed in exponential notation
 * @param {number} number 
 * @returns {boolean} `true` if expressed as exponent, `false` if regular number
 */
export const isExponential = number => {
    let str = number.toString();
    return ( str.includes( EXPONENT_NEGATIVE ) || str.includes( EXPONENT_POSITIVE ) );
}

/**
 * @name parseNumber
 * @constant
 * @function
 * @description Converts number into a magnitude
 * @param {number} number 
 * @returns {string} Number converted to magnitude
 */
export const parseNumber = number => {
    let test = toFixed( number ),
    needsConverting = isMagnitude( number ),
    absolute = Math.abs( number );

    if( number.toString() === '0' ) return number;

    // if the value isn't a number and it isn't a magnitude, leave
    if( isNaN( test ) && !needsConverting ){
        return number;
    
    // if the value is a magnitude, convert it to a number (Required for converting 1000M to 1G)
    } else if( needsConverting ){
        number = convertMagnitude( number );
        absolute = Math.abs( number );
    }

    let magnitude = MAGNITUDES.find( magnitude => ( magnitude.min <= absolute && magnitude.max >= absolute ) );

    if( !magnitude ) return Number( number );
    let dividend = magnitude.divisor ? ( number / magnitude.divisor ) : ( number / magnitude.min );
    return round( dividend ) + magnitude.symbol;
}

/**
 * @name parseMagnitude
 * @constant
 * @function
 * @description Converts magnitude into a number (for display purposes)
 * @param {string} mag 
 * @returns {number} magnitude converted to a number
 */
export const parseMagnitude = mag => {
    if( typeof mag === 'number' ) return toFixed( mag );
    let number = convertMagnitude( mag );
    return isExponential( number ) ? toFixed( number ) : number;
}

/**
 * @name round
 * @constant
 * @function
 * @description Rounds a number to 2 decimal points
 */
export const round = number => {

    let trunc = parseFloat( Math.round( number * 100 ) / 100 ).toFixed( 2 ),
    index = trunc.indexOf( '.' );

    if( index > -1 && trunc.substr( index ).length > MAX_DECIMAL_PLACE ) trunc = trunc.substr( 0, ( index + MAX_DECIMAL_PLACE ) );

    return Number( trunc );
}

/**
 * @name toFixed
 * @constant
 * @function
 * @description Converts the scientific expression to a full number
 * @param {*} number
 */
export const toFixed = number => {
    if( Math.abs( number ) < 1.0){
        let e = parseInt( number.toString().split('e-')[1]);
        if( e < 7){
            number *= Math.pow(10,e-1);
            number = '0.' + (new Array(e)).join('0') + number.toString().substring(2);
        } else {
            return number;
        }
    } else {
        let e = parseInt( number.toString().split('+')[1]);
        if( e > 20 ){
            e -= 20;
            number /= Math.pow(10,e);
            number += (new Array(e+1)).join('0');
        }
    }
    return round( number );
}